import { forwardRef } from 'react';
import { Sparkle } from '@phosphor-icons/react';

/**
 * ShareCard — a 1080x1920 Instagram-Story-sized card rendered off-screen
 * and captured by html-to-image. The component itself takes no visible room;
 * keep it mounted but hide it with `pointerEvents: none` + absolute off-screen
 * position so html-to-image can still measure it accurately.
 */
export const ShareCard = forwardRef(function ShareCard({ quote, forName, tag, date }, ref) {
  // Tag-tinted gradient — keeps the card on-brand but visually varied.
  const GRADIENTS = {
    focus:     'linear-gradient(135deg, #0b1220 0%, #1e1b4b 45%, #7c1d6f 100%)',
    money:     'linear-gradient(135deg, #052e1f 0%, #064e3b 50%, #065f46 100%)',
    calm:      'linear-gradient(135deg, #0b1220 0%, #0c4a6e 50%, #0ea5e9 100%)',
    momentum:  'linear-gradient(135deg, #0b1220 0%, #581c87 50%, #a21caf 100%)',
    curiosity: 'linear-gradient(135deg, #0b1220 0%, #831843 50%, #be185d 100%)',
  };
  const gradient = GRADIENTS[tag] || GRADIENTS.focus;

  const prettyDate = date
    ? new Date(date).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
    : '';

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        left: '-99999px',
        top: 0,
        width: 1080,
        height: 1920,
        background: gradient,
        color: '#fff',
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        overflow: 'hidden',
        isolation: 'isolate',
      }}
      aria-hidden
    >
      {/* soft radial glow */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.14), transparent 55%)',
      }} />
      {/* subtle grid noise */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage:
          'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),' +
          'linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
        maskImage: 'radial-gradient(circle at center, black 40%, transparent 85%)',
      }} />

      {/* Header (brand) */}
      <div style={{
        position: 'absolute', top: 80, left: 80, right: 80,
        display: 'flex', alignItems: 'center', gap: 18,
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: 18,
          background: 'rgba(255,255,255,0.14)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(8px)',
        }}>
          <Sparkle size={34} weight="fill" color="#fff" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 30, fontWeight: 800, letterSpacing: -0.5 }}>LifeAdmin</span>
          <span style={{ fontSize: 15, color: 'rgba(255,255,255,0.64)', letterSpacing: 3, textTransform: 'uppercase', fontWeight: 700 }}>
            AI Chief of Staff
          </span>
        </div>
      </div>

      {/* Giant open-quote mark */}
      <div style={{
        position: 'absolute', top: 240, left: 80,
        fontSize: 420, lineHeight: 1, fontFamily: 'Georgia, serif',
        color: 'rgba(255,255,255,0.08)', fontWeight: 700,
        userSelect: 'none',
      }}>
        &ldquo;
      </div>

      {/* The quote — centre-anchored, large, restrained */}
      <div style={{
        position: 'absolute',
        top: '46%',
        left: 100,
        right: 100,
        transform: 'translateY(-50%)',
        textAlign: 'left',
      }}>
        <p style={{
          fontSize: 66,
          lineHeight: 1.22,
          fontWeight: 600,
          letterSpacing: -1.2,
          margin: 0,
          color: '#fff',
          textWrap: 'balance',
        }}>
          {quote}
        </p>
      </div>

      {/* Footer attribution */}
      <div style={{
        position: 'absolute', bottom: 120, left: 80, right: 80,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{
            fontSize: 16, letterSpacing: 4, textTransform: 'uppercase',
            fontWeight: 700, color: 'rgba(255,255,255,0.55)',
          }}>
            A daily quote for {forName}
          </span>
          {prettyDate && (
            <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.82)', fontWeight: 500 }}>
              {prettyDate}
            </span>
          )}
        </div>
        <div style={{
          padding: '12px 22px', borderRadius: 999,
          background: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.22)',
          fontSize: 20, fontWeight: 600,
          backdropFilter: 'blur(6px)',
        }}>
          lifeadmin.ai
        </div>
      </div>
    </div>
  );
});
